import { CollabViewSortType } from '@databases/model/views/view-sort';
import {
  SortArray,
  MutableSortArray,
  Sort,
  MutableSort,
} from '@databases/model/views/view-sort';
import * as Y from 'yjs';

import { LazyValue } from '../helpers/lazy-value';
import { SynchronizedArray } from '../helpers/synchronized-array';
import { SynchronizedRecord } from '../helpers/synchronized-record';
import { DisposableElement, WrappedYType } from '../helpers/types';

const SortProperties = {
  ascending: 'a',
  fieldId: 'fi',
};

export class YSort
  extends SynchronizedRecord
  implements DisposableElement, WrappedYType, Sort, MutableSort
{
  static mapper(value: unknown): YSort | undefined {
    if (value instanceof Y.Map) {
      return new YSort(value);
    }
  }

  get ascending(): boolean {
    return this.getBoolean(SortProperties.ascending, false);
  }

  set ascending(ascending: boolean) {
    this.set(SortProperties.ascending, ascending);
  }

  get fieldId(): string {
    return this.getString(SortProperties.fieldId, '');
  }

  set fieldId(fieldId: string) {
    this.set(SortProperties.fieldId, fieldId);
  }

  toJSON(): CollabViewSortType {
    return {
      ascending: this.ascending,
      fieldId: this.fieldId,
    };
  }

  static fromJSON(json: CollabViewSortType): YSort {
    return new YSort(
      new Y.Map([
        [SortProperties.ascending, json.ascending],
        [SortProperties.fieldId, json.fieldId],
      ])
    );
  }
}

export class YSortArray
  implements DisposableElement, WrappedYType, SortArray, MutableSortArray
{
  private sortArray: SynchronizedArray<YSort>;

  constructor(input: Y.Array<unknown> | LazyValue<Y.Array<unknown>>) {
    this.sortArray = new SynchronizedArray(input, YSort.mapper);
  }

  insertSort(index: number, sort: CollabViewSortType): void {
    this.sortArray.insert(index, [YSort.fromJSON(sort)]);
  }

  appendSort(sort: CollabViewSortType): void {
    this.sortArray.push([YSort.fromJSON(sort)]);
  }

  deleteSortForFieldId(fieldId: string): void {
    const index = this.sortArray
      .toArray()
      .findIndex((ySort) => ySort.toJSON().fieldId === fieldId);

    if (index === -1) {
      return;
    }

    this.sortArray.delete(index);
  }

  deleteSortForFieldIds(fieldIds: string[]): void {
    fieldIds.forEach((fieldId) => this.deleteSortForFieldId(fieldId));
  }

  clearAll(): void {
    this.sortArray = new SynchronizedArray(Y.Array.from<any>([]), YSort.mapper);
  }

  updateSortForFieldId(fieldId: string, ascending: boolean): void {
    const index = this.sortArray
      .toArray()
      .findIndex((ySort) => ySort.toJSON().fieldId === fieldId);

    if (index === -1) {
      return;
    }

    this.sortArray.get(index).ascending = ascending;
  }

  reorderSortForFieldId(fieldId: string, newIndex: number): void {
    const index = this.sortArray
      .toArray()
      .findIndex((ySort) => ySort.toJSON().fieldId === fieldId);

    if (index === -1) {
      return;
    }

    const sort = this.sortArray.get(index).toJSON();
    this.sortArray.delete(index);
    this.sortArray.insert(newIndex, [YSort.fromJSON(sort)]);
  }

  unwrap(): Y.AbstractType<string> | undefined {
    return this.sortArray.unwrap();
  }

  dispose(): void {
    this.sortArray.dispose();
  }

  get length(): number {
    return this.sortArray.length;
  }

  toArray(): CollabViewSortType[] {
    return this.sortArray.toArray().map((ySort) => ySort.toJSON());
  }

  static fromJSON(json: CollabViewSortType[]): YSortArray {
    return new YSortArray(
      Y.Array.from<any>(json.map((sort) => YSort.fromJSON(sort).unwrap()))
    );
  }
}
